import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import { Loader2, Users, Plus, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ClientsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", industry: "" });

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () =>
    axios.get(`${API_URL}/api/clients`, auth)
      .then(({ data }) => setClients(data))
      .catch(() => toast.error("Impossible de charger les clients"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const createClient = async () => {
    if (!form.company_name || !form.contact_name || !form.email) {
      toast.error("Société, contact et email sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/clients`, form, auth);
      toast.success("Client créé");
      setShowNew(false);
      setForm({ company_name: "", contact_name: "", email: "", phone: "", industry: "" });
      navigate(`/dashboard/opportunities/${data.opportunity_id}`);
    } catch (e) {
      toast.error("Erreur lors de la création du client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Clients</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Vos affaires gagnées — une seule fiche du premier contact au projet
            </p>
          </div>
          <div className="flex-1" />
          <Button onClick={() => setShowNew(true)} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-1.5" /> Nouveau client
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : clients.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
            <Users className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            Aucun client pour le moment.<br />
            <span className="text-xs">Une opportunité passée en « Gagné » apparaît ici automatiquement.</span>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3 font-bold">Société</th>
                  <th className="px-5 py-3 font-bold">Contact</th>
                  <th className="px-5 py-3 font-bold">Email</th>
                  <th className="px-5 py-3 font-bold">Téléphone</th>
                  <th className="px-5 py-3 font-bold">Secteur</th>
                  <th className="px-5 py-3 font-bold text-right">Projets</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id}
                    onClick={() => navigate(`/dashboard/opportunities/${c.opportunity_id}`)}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
                    data-testid={`client-row-${c.id}`}>
                    <td className="px-5 py-3.5 font-semibold text-slate-900">{c.company_name}</td>
                    <td className="px-5 py-3.5 text-slate-600">{c.contact_name || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-500">{c.email || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-500">{c.phone || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-500">{c.industry || "—"}</td>
                    <td className="px-5 py-3.5 text-right tabular-nums text-slate-600">{c.projects_count || 0}</td>
                    <td className="px-5 py-3.5 text-right text-slate-300">
                      <ArrowRight className="w-4 h-4 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showNew && (
          <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900">Nouveau client</h2>
                <button onClick={() => setShowNew(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-700">Société *</label>
                  <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Contact *</label>
                  <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Email *</label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Téléphone</label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Secteur</label>
                  <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="BTP, restauration…" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <Button variant="outline" onClick={() => setShowNew(false)}>Annuler</Button>
                <Button onClick={createClient} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                  {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Créer
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CRMLayout>
  );
}
